import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  InputAdornment,
  Toolbar,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { statusPillSx, surfaceCardSx, tableHeadCellSx, tokens } from '../../theme/tokens';

export default function HRElections() {
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingElection, setEditingElection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    start_time: '08:00',
    end_time: '17:00',
    position_ids: [],
  });
  const [positions, setPositions] = useState([]);
  const [selectedElection, setSelectedElection] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'results'
  const [submitting, setSubmitting] = useState(false);
  const [kioskDialog, setKioskDialog] = useState(null); // election or null
  const [kioskPasscode, setKioskPasscode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchElections();
    fetchPositions();
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchElections();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchElections = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/hr/elections', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      });
      setElections(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch elections');
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/hr/positions', {
        params: { limit: 1000, active: true },
      });
      setPositions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const fetchElectionResults = async (electionId) => {
    try {
      const response = await api.get(`/hr/elections/${electionId}/results`);
      setSelectedElection(response.data.data);
      setViewMode('results');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch election results');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (election = null) => {
    setEditingElection(election);
    setFormData({
      name: election?.name || '',
      description: election?.description || '',
      start_date: election?.start_date ? election.start_date.split('T')[0] : '',
      end_date: election?.end_date ? election.end_date.split('T')[0] : '',
      start_time: election?.start_time || '08:00',
      end_time: election?.end_time || '17:00',
      position_ids: election?.positions?.map((p) => p.id) || [],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingElection(null);
    setSubmitting(false);
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      start_time: '08:00',
      end_time: '17:00',
      position_ids: [],
    });
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        start_time: formData.start_time?.slice(0, 5) || '08:00',
        end_time: formData.end_time?.slice(0, 5) || '17:00',
        position_ids: formData.position_ids,
      };
      if (editingElection) {
        await api.put(`/hr/elections/${editingElection.id}`, data);
      } else {
        await api.post('/hr/elections', data);
      }
      handleCloseDialog();
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save election');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndElection = async (election) => {
    if (!window.confirm(`End the election "${election.name}" now? This will close voting, determine winners, and install them as executives.`)) {
      return;
    }
    try {
      await api.post(`/hr/elections/${election.id}/end`);
      fetchElections();
      if (selectedElection?.election?.id === election.id) {
        setSelectedElection(null);
        setViewMode('list');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to end election');
    }
  };

  const handlePauseElection = async (election) => {
    try {
      await api.post(`/hr/elections/${election.id}/pause`);
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to pause election');
    }
  };

  const handleResumeElection = async (election) => {
    try {
      await api.post(`/hr/elections/${election.id}/resume`);
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resume election');
    }
  };

  const handleSaveKioskPasscode = async () => {
    if (!kioskDialog) return;
    if (!kioskPasscode || kioskPasscode.length < 4) {
      setError('Kiosk passcode must be at least 4 characters');
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/hr/elections/${kioskDialog.id}/kiosk-passcode`, { passcode: kioskPasscode });
      setKioskDialog(null);
      setKioskPasscode('');
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set kiosk passcode');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearKioskPasscode = async () => {
    if (!kioskDialog) return;
    if (!window.confirm('Disable kiosk voting for this election? Existing station sessions will keep working until they expire.')) {
      return;
    }
    try {
      setSubmitting(true);
      await api.delete(`/hr/elections/${kioskDialog.id}/kiosk-passcode`);
      setKioskDialog(null);
      setKioskPasscode('');
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear kiosk passcode');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnstopElection = async (election) => {
    if (!window.confirm(`Reopen the election "${election.name}"? This will remove the installed winners, restore the previous executives, and make voting active again.`)) {
      return;
    }
    try {
      await api.post(`/hr/elections/${election.id}/unstop`);
      fetchElections();
      if (selectedElection?.election?.id === election.id) {
        setSelectedElection(null);
        setViewMode('list');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reopen election');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this election? This will also delete all associated votes and candidates.')) {
      return;
    }
    try {
      await api.delete(`/hr/elections/${id}`);
      fetchElections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete election');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const liveStatus = (status) => {
    if (status === 'active') return 'active';
    if (status === 'upcoming') return 'pending';
    return 'inactive';
  };

  if (viewMode === 'results' && selectedElection) {
    const isLive = selectedElection.election.status === 'active';
    return (
      <Box>
        <PageHeader
          crumbs={
            <>
              Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Elections</Box>
            </>
          }
          title={selectedElection.election.name}
          actions={
            <Button
              variant="outlined"
              onClick={() => { setViewMode('list'); setSelectedElection(null); }}
              sx={{ borderColor: tokens.border, color: tokens.ink }}
            >
              Back to Elections
            </Button>
          }
        />

        <Box
          sx={{
            bgcolor: tokens.ink,
            color: '#F3F6F4',
            borderRadius: tokens.radius,
            p: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 17, fontWeight: 600 }}>
              Live results
            </Typography>
            <Typography sx={{ color: '#A8B5AE', fontSize: 13, mt: 0.35 }}>
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {selectedElection.total_voters}
              </Box>
              {' '}voters · {selectedElection.positions?.length || 0} positions
            </Typography>
          </Box>
          {isLive && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                bgcolor: 'rgba(217,164,65,.15)',
                color: tokens.accent,
                px: 1.5,
                py: 0.6,
                borderRadius: 99,
                fontWeight: 600,
                fontSize: 12.5,
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.accent }} />
              Voting in progress
            </Box>
          )}
        </Box>

        <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total voters
              </Typography>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {selectedElection.total_voters}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Positions
              </Typography>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {selectedElection.positions?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                Status
              </Typography>
              <Box sx={statusPillSx(liveStatus(selectedElection.election.status))}>
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                {selectedElection.election.status}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {selectedElection.positions?.map((position) => (
          <Paper key={position.id} elevation={0} sx={{ ...surfaceCardSx, mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}`, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{position.name}</Typography>
              <Typography sx={{ color: tokens.muted, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                Total votes: {position.total_votes}
                {((position.abstain_count || 0) + (position.reject_count || 0)) > 0 && (
                  <>
                    {(position.reject_count || 0) > 0 && <> · No (did not vote for): {position.reject_count || 0}</>}
                    {(position.abstain_count || 0) > 0 && <> · Abstain: {position.abstain_count || 0}</>}
                  </>
                )}
              </Typography>
            </Box>
            <Box sx={{ px: 2.5, py: 2 }}>
              {position.candidates?.map((candidate, idx) => {
                const pct = candidate.vote_percentage != null ? Number(candidate.vote_percentage) : 0;
                const lead = idx === 0;
                return (
                  <Box
                    key={candidate.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '150px 1fr 52px' },
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1,
                      fontSize: 13,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {candidate.first_name} {candidate.last_name}
                      {selectedElection.election?.status === 'completed' && idx === 0 && (
                        <Box sx={statusPillSx('active')}>Winner</Box>
                      )}
                    </Box>
                    <Box sx={{ height: 9, bgcolor: '#EDF1EE', borderRadius: 99, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${Math.min(100, Math.max(0, pct))}%`,
                          height: '100%',
                          bgcolor: lead ? tokens.accent : tokens.primary,
                          borderRadius: 99,
                        }}
                      />
                    </Box>
                    <Typography sx={{ textAlign: { sm: 'right' }, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {candidate.vote_percentage != null ? `${candidate.vote_percentage}%` : '0%'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Elections</Box>
          </>
        }
        title="Elections"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
          >
            Create election
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, mb: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', px: 2.5, py: 1.75, borderBottom: `1px solid ${tokens.border}` }}>
          <TextField
            placeholder="Search elections…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 220, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', '& fieldset': { borderColor: tokens.border } } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: tokens.muted }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ m: 2, borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <TableContainer>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: tokens.primary }} />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Election</TableCell>
                  <TableCell sx={tableHeadCellSx}>Start</TableCell>
                  <TableCell sx={tableHeadCellSx}>End</TableCell>
                  <TableCell sx={tableHeadCellSx}>Status</TableCell>
                  <TableCell sx={tableHeadCellSx}>Positions</TableCell>
                  <TableCell sx={tableHeadCellSx}>Votes</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {elections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 5.5, color: tokens.muted }}>
                        <Typography sx={{ fontWeight: 600, color: tokens.ink, mb: 0.5 }}>No elections yet</Typography>
                        <Typography variant="body2">Create an election to open voting.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  elections.map((election) => (
                    <TableRow key={election.id} hover sx={{ '&:hover': { bgcolor: '#F4F7F5' }, '& td': { borderColor: tokens.border } }}>
                      <TableCell>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                          {election.name}
                        </Typography>
                        {election.description && (
                          <Typography sx={{ fontSize: 12, color: tokens.muted }}>
                            {election.description.substring(0, 50)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13.5 }}>{formatDate(election.start_date)}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13.5 }}>{formatDate(election.end_date)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Box sx={statusPillSx(liveStatus(election.status))}>
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                            {election.status}
                          </Box>
                          {election.is_paused && (
                            <Box sx={statusPillSx('pending')}>Paused</Box>
                          )}
                          {election.kiosk_enabled && (
                            <Box sx={statusPillSx('active')}>Kiosk</Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13.5 }}>
                        {election.positions?.length || 0}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13.5 }}>
                        {election.total_votes || 0}
                      </TableCell>
                      <TableCell align="right">
                        {(election.status === 'active' || election.status === 'upcoming') && !election.is_paused && (
                          <IconButton
                            size="small"
                            onClick={() => handlePauseElection(election)}
                            title="Pause Election"
                            color="info"
                          >
                            <PauseCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        {(election.status === 'active' || election.status === 'upcoming') && election.is_paused && (
                          <IconButton
                            size="small"
                            onClick={() => handleResumeElection(election)}
                            title="Resume Election"
                            color="success"
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        )}
                        {(election.status === 'active' || election.status === 'upcoming') && (
                          <IconButton
                            size="small"
                            onClick={() => handleEndElection(election)}
                            title="End Election"
                            color="warning"
                          >
                            <StopCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        {election.status === 'completed' && (
                          <IconButton
                            size="small"
                            onClick={() => handleUnstopElection(election)}
                            title="Reopen Election"
                            color="success"
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => {
                            setKioskDialog(election);
                            setKioskPasscode('');
                          }}
                          title="Kiosk station passcode"
                          color={election.kiosk_enabled ? 'success' : 'default'}
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/hr/elections/${election.id}/candidates`)}
                          title="Manage Candidates"
                          color="primary"
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => fetchElectionResults(election.id)}
                          title="View Results"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(election)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(election.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600 }}>
          {editingElection ? 'Edit Election' : 'Add Election'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Election Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Closing Time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Positions</InputLabel>
                <Select
                  multiple
                  value={formData.position_ids}
                  onChange={(e) => setFormData({ ...formData, position_ids: e.target.value })}
                  label="Positions"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const position = positions.find((p) => p.id === value);
                        return <Chip key={value} label={position?.name || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {positions.map((position) => (
                    <MenuItem key={position.id} value={position.id}>
                      {position.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.start_date || !formData.end_date || submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!kioskDialog} onClose={() => !submitting && setKioskDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Kiosk station passcode</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {kioskDialog?.name}. Poll workers enter this passcode at <strong>/kiosk</strong> to arm a laptop.
            {kioskDialog?.kiosk_enabled ? ' A passcode is already set — enter a new one to rotate it.' : ''}
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Station passcode"
            value={kioskPasscode}
            onChange={(e) => setKioskPasscode(e.target.value)}
            autoComplete="new-password"
            helperText="Minimum 4 characters"
          />
        </DialogContent>
        <DialogActions>
          {kioskDialog?.kiosk_enabled && (
            <Button color="warning" onClick={handleClearKioskPasscode} disabled={submitting}>
              Disable kiosk
            </Button>
          )}
          <Button onClick={() => setKioskDialog(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveKioskPasscode} disabled={submitting || kioskPasscode.length < 4}>
            {submitting ? 'Saving…' : 'Save passcode'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
